export type OrderStatus = "unfulfilled" | "on_hold" | "cancelled" | "partially_fulfilled" | "fulfilled";

export type ScenarioName =
  | "normal"
  | "quantity-three"
  | "missing-barcode"
  | "missing-images"
  | "unicode-address"
  | "long-order"
  | "multiple-orders"
  | "empty-queue"
  | "attention-statuses"
  | "multi-page"
  | "load-failure"
  | "stale-refresh"
  | "wrong-duplicate-ambiguous"
  | "timeout-after-fulfillment"
  | "external-change"
  | "fulfillment-rejection";

export const SCENARIOS: ReadonlyArray<{ id: ScenarioName; label: string; description: string }> = [
  { id: "normal", label: "Normal mixed order", description: "A small mixed clothing order for the happy path." },
  { id: "quantity-three", label: "Quantity three", description: "Three identical physical units, each confirmed separately." },
  { id: "missing-barcode", label: "Missing barcode", description: "The current variant has no barcode and requires manual confirmation." },
  { id: "missing-images", label: "Missing images", description: "Variant and product images are absent; a clean placeholder is shown." },
  { id: "unicode-address", label: "Unicode address", description: "Long notes and a bidirectional customer/address example." },
  { id: "long-order", label: "30-unit order", description: "A large order used to exercise the bounded item strip." },
  { id: "multiple-orders", label: "Multiple orders", description: "Three eligible orders so the queue controls can be rehearsed." },
  { id: "empty-queue", label: "Empty queue", description: "No eligible unfulfilled orders are available." },
  { id: "attention-statuses", label: "Attention statuses", description: "Eligible work plus held/cancelled orders for the warning banner." },
  { id: "multi-page", label: "Multi-page queue", description: "Many eligible orders; the adapter must aggregate every page." },
  { id: "load-failure", label: "Load failure", description: "Shopify order refresh fails with a retryable error." },
  { id: "stale-refresh", label: "Stale refresh", description: "The first load works, then a manual refresh fails and retained data is marked stale." },
  { id: "wrong-duplicate-ambiguous", label: "Wrong / duplicate / ambiguous", description: "A scan playground with wrong, duplicate and shared-barcode cases." },
  { id: "timeout-after-fulfillment", label: "Unknown fulfillment result", description: "Shopify commits fulfillment, but the response is unknown until reconciled." },
  { id: "external-change", label: "External order change", description: "Fulfillment detects a changed provider version and blocks a blind retry." },
  { id: "fulfillment-rejection", label: "Fulfillment rejection", description: "Shopify rejects fulfillment; the operator can retry after recovery." },
];

export interface VariantDetails {
  productId: string;
  variantId: string;
  productName: string;
  /** Shopify's product handle; useful for matching catalog naming conventions such as color slugs. */
  productHandle: string | null;
  sku: string;
  barcode: string | null;
  imageUrl: string | null;
  productImageUrl: string | null;
  color: string | null;
  size: string | null;
  options: Record<string, string>;
  /** Optional because the packing queue does not need catalog pricing. */
  unitPrice?: Money;
}

export interface Money {
  /** Integer amount in the currency's minor unit; never use a floating point amount for arithmetic. */
  amountMinor: number;
  currency: string;
}

export interface OnsiteVariant {
  variantId: string;
  productId: string;
  barcode: string;
  sku: string;
  name: string;
  /** Shopify product handle, when the provider can return it for barcode diagnostics. */
  productHandle?: string | null;
  imageUrl: string | null;
  productImageUrl: string | null;
  options: Record<string, string>;
  unitPrice: Money;
}

export interface OnsiteCartItem extends OnsiteVariant {
  quantity: number;
}

export interface OnsiteBuyer {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
}

export interface OnsiteSaleItemInput {
  variantId: string;
  quantity: number;
}

export interface OnsiteSaleRequest {
  operationKey: string;
  items: OnsiteSaleItemInput[];
  buyer: OnsiteBuyer | null;
  currency: string;
  expectedSubtotalMinor: number;
  totalPaidMinor: number;
}

export interface OnsiteSaleResult {
  orderId: string;
  orderNumber: string;
  itemCount: number;
  totalPaidMinor: number;
  currency: string;
  operationKey: string;
  inventoryStatus: "decremented";
}

export interface OrderLine {
  lineId: string;
  fulfillableQuantity: number;
  originalQuantity: number;
  variant: VariantDetails;
}

export interface PickUnit {
  unitId: string;
  lineId: string;
  instanceNumber: number;
  variant: VariantDetails;
}

export interface ShippingAddress {
  recipientName: string;
  phone: string;
  postalCode: string;
  city: string;
  addressLines: string[];
  street: string;
  building: string;
  unit: string;
  country: string;
  deliveryNote: string;
}

export interface Order {
  id: string;
  orderNumber: string;
  customerName: string;
  createdAt: string;
  note: string;
  currency: string;
  total: number;
  financialStatus: string;
  status: OrderStatus;
  statusLabel: string;
  eligible: boolean;
  attentionReason: string | null;
  version: number;
  shippingAddress: ShippingAddress;
  lines: OrderLine[];
  units: PickUnit[];
}

export interface AddressFieldErrors {
  recipientName?: string;
  phone?: string;
  postalCode?: string;
  city?: string;
  street?: string;
  building?: string;
  addressLines?: string;
  country?: string;
  [field: string]: string | undefined;
}

export type ErrorCategory = "validation" | "unavailable" | "rate-limited" | "conflict" | "unknown";

export interface ApiErrorPayload {
  category: ErrorCategory;
  code: string;
  message: string;
  retryable: boolean;
  fieldErrors?: AddressFieldErrors;
  operationKey?: string;
  retryAfterSeconds?: number;
}

export interface OrdersResponse {
  orders: Order[];
  attention: {
    count: number;
    statuses: string[];
  };
  refreshedAt: string;
  mock: boolean;
  mode: "mock" | "shopify";
  scenario: ScenarioName;
}

export interface ConfigResponse {
  mode: "mock" | "shopify";
  scenario: ScenarioName;
  scenarios: ReadonlyArray<{ id: ScenarioName; label: string; description: string }>;
  shopifyAdminUrl: string;
  shopifyFulfillmentWritesEnabled: boolean;
  shopifyOnsiteSalesEnabled: boolean;
}

export interface OperationSuccess<T> {
  status: "success";
  data: T;
}

export interface OperationUnknown {
  status: "unknown";
  operationKey: string;
  message: string;
  reconciliationAvailable: boolean;
}

export type OnsiteVariantLookup =
  | { status: "found"; variant: OnsiteVariant }
  | { status: "not-found"; barcode: string }
  | { status: "ambiguous"; barcode: string; variants: OnsiteVariant[] };

export function expandLineUnits(line: OrderLine): PickUnit[] {
  return Array.from({ length: Math.max(0, line.fulfillableQuantity) }, (_, index) => ({
    unitId: `${line.lineId}:unit-${index + 1}`,
    lineId: line.lineId,
    instanceNumber: index + 1,
    variant: line.variant,
  }));
}

export function expandOrderUnits(lines: OrderLine[]): PickUnit[] {
  return lines.flatMap(expandLineUnits);
}

export function variantKey(variant: VariantDetails): string {
  return variant.variantId || `${variant.productId}:${variant.productName}:${variant.color ?? ""}:${variant.size ?? ""}`;
}

export type BarcodeMatch =
  | { kind: "matched"; unit: PickUnit }
  | { kind: "ambiguous"; candidates: PickUnit[]; scanned: string }
  | { kind: "duplicate"; scanned: string; confirmedUnit: PickUnit }
  | { kind: "wrong"; scanned: string; expected: PickUnit | null; reason: "no-match" | "missing-barcode" };

export function matchBarcode(units: PickUnit[], confirmedUnitIds: ReadonlySet<string>, scannedValue: string): BarcodeMatch {
  const scanned = scannedValue.trim();
  const remaining = units.filter((unit) => !confirmedUnitIds.has(unit.unitId));
  const candidates = remaining.filter((unit) => unit.variant.barcode !== null && unit.variant.barcode === scanned);
  const distinctVariants = new Set(candidates.map((unit) => variantKey(unit.variant)));

  if (candidates.length > 0 && distinctVariants.size > 1) {
    return { kind: "ambiguous", candidates, scanned };
  }

  if (candidates.length > 0) {
    return { kind: "matched", unit: candidates[0] };
  }

  const confirmedMatch = units.find((unit) => confirmedUnitIds.has(unit.unitId) && unit.variant.barcode === scanned);
  if (confirmedMatch) {
    return { kind: "duplicate", scanned, confirmedUnit: confirmedMatch };
  }

  const missingBarcodeRemaining = remaining.find((unit) => unit.variant.barcode === null);
  return {
    kind: "wrong",
    scanned,
    expected: remaining[0] ?? null,
    reason: missingBarcodeRemaining && remaining.every((unit) => unit.variant.barcode === null) ? "missing-barcode" : "no-match",
  };
}

export function validateAddress(address: ShippingAddress): AddressFieldErrors {
  const errors: AddressFieldErrors = {};
  if (!address.recipientName.trim()) errors.recipientName = "Recipient name is required.";
  if (!address.phone.trim()) errors.phone = "Phone is required.";
  if (!address.city.trim()) errors.city = "City is required.";
  if (!address.country.trim()) errors.country = "Country is required.";
  if (!address.addressLines.some((line) => line.trim())) errors.addressLines = "At least one address line is required.";
  return errors;
}

export function countConfirmedUnits(units: PickUnit[], confirmedIds: ReadonlySet<string>): number {
  return units.reduce((count, unit) => count + (confirmedIds.has(unit.unitId) ? 1 : 0), 0);
}

export function currencyMinorDigits(currency: string): number {
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency: currency.toUpperCase() }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

export function parseMoneyToMinorUnits(value: string, currency: string): number | null {
  const normalized = value.trim();
  const digits = currencyMinorDigits(currency);
  const pattern = digits === 0 ? /^(?:0|[1-9]\d*)$/ : new RegExp(`^(?:0|[1-9]\\d*)(?:\\.\\d{1,${digits}})?$`);
  if (!pattern.test(normalized)) return null;
  const [wholePart, fractionPart = ""] = normalized.split(".");
  const factor = 10 ** digits;
  const amount = Number(wholePart) * factor + Number((fractionPart + "0".repeat(digits)).slice(0, digits) || 0);
  return Number.isSafeInteger(amount) && amount >= 0 ? amount : null;
}

export function minorUnitsToDecimalString(amountMinor: number, currency: string): string {
  if (!Number.isSafeInteger(amountMinor) || amountMinor < 0) throw new Error("Money amount must be a non-negative safe integer.");
  const digits = currencyMinorDigits(currency);
  if (digits === 0) return String(amountMinor);
  const factor = 10 ** digits;
  const whole = Math.floor(amountMinor / factor);
  const fraction = String(amountMinor % factor).padStart(digits, "0");
  return `${whole}.${fraction}`;
}

export function formatMinorMoney(amountMinor: number, currency: string): string {
  const digits = currencyMinorDigits(currency);
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency: currency.toUpperCase(), minimumFractionDigits: digits, maximumFractionDigits: digits }).format(amountMinor / (10 ** digits));
  } catch {
    return `${currency.toUpperCase()} ${minorUnitsToDecimalString(amountMinor, currency)}`;
  }
}

export function addOnsiteCartItem(items: OnsiteCartItem[], variant: OnsiteVariant): OnsiteCartItem[] {
  const existing = items.find((item) => item.variantId === variant.variantId);
  if (!existing) return [...items, { ...variant, quantity: 1 }];
  return items.map((item) => item.variantId === variant.variantId ? { ...variant, quantity: item.quantity + 1 } : item);
}

export function setOnsiteCartItemQuantity(items: OnsiteCartItem[], variantId: string, quantity: number): OnsiteCartItem[] {
  if (!Number.isFinite(quantity)) return items;
  const normalized = Math.max(0, Math.floor(quantity));
  return normalized === 0
    ? items.filter((item) => item.variantId !== variantId)
    : items.map((item) => item.variantId === variantId ? { ...item, quantity: normalized } : item);
}

export function onsiteItemCount(items: OnsiteCartItem[]): number {
  return items.reduce((count, item) => count + item.quantity, 0);
}

export function onsiteSubtotalMinor(items: OnsiteCartItem[]): number {
  return items.reduce((total, item) => total + item.unitPrice.amountMinor * item.quantity, 0);
}
